import { i18nTranslationHelper } from '@/i18n'
import {
  EnrollmentTimelineStepAPI,
  EnrollmentStepItem,
  EnrollmentDetailAPIResponse,
  EnrollmentDetailDataResponse,
  AuthorizationInfoAPI,
  AuthorizationInfo,
  CourseDetailsInfoAPI,
  CourseDetailsInfo,
  CycleAPI,
  Cycle,
  SkillAPI,
  Skill
} from './enrollment-steps-service-types'
import { Category } from '@/domain/models/content/category'
import { Subtype } from '@/domain/models/content/subtype'
import { ContentFormat } from '@/domain/models/content/content-format'
import {
  EnrollmentStatus,
  EnrollmentStepStatusEnum,
  EnrollmentStepTypeEnum
} from '@/domain/models/enrollment/status'
import { Shift } from '@/domain/models/content/shift'
import { formatDateString, formatDateTime, formatTime } from '@/main/helpers'
import { formatMoney } from '@/presentation/helpers/page/form-helper'

const t = i18nTranslationHelper.getTComponents

// Step and Status Mapping
export const getStepTitle = (
  stepType: EnrollmentStepTypeEnum,
  status: EnrollmentStepStatusEnum
): string => t(`enrollmentSteps.${stepType.toLowerCase()}.${status}.title`)

export const getStepDescription = (
  stepType: EnrollmentStepTypeEnum,
  status: EnrollmentStepStatusEnum
): string => t(`enrollmentSteps.${stepType.toLowerCase()}.${status}.description`)

export const getStatusLabel = (status: EnrollmentStepStatusEnum): string => {
  const statusMap: Record<EnrollmentStepStatusEnum, string> = {
    COMPLETED: t('enrollmentWorkflowTimeline.status.completed'),
    IN_PROGRESS: t('enrollmentWorkflowTimeline.status.inProgress'),
    DISABLED: t('enrollmentWorkflowTimeline.status.pending'),
    ERROR: t('enrollmentWorkflowTimeline.status.rejected')
  }
  return statusMap[status] || status
}

// Transformation Functions
const transformAuthorizationInfo = (apiData: AuthorizationInfoAPI): AuthorizationInfo => {
  return {
    id: apiData.id ?? 0,
    productId: apiData.productId ?? 0,
    name: apiData.name ?? '',
    status: apiData.status ?? 0,
    institutionId: apiData.institutionId ?? 0,
    institutionName: apiData.institutionName ?? '',
    institutionLogo: apiData.institutionLogo ?? '',
    institutionUserPlatformUrl: apiData.institutionUserPlatformUrl,
    companyName: apiData.companyName ?? '',
    subTypeSubscription: apiData.subTypeSubscription ?? null,
    createdAt: formatDateString(apiData.createdAt || ''),
    approvedAt: formatDateString(apiData.approvedAt || ''),
    expiresAt: formatDateString(apiData.expiresAt || ''),
    category: apiData.categoryId ? Category.getById(apiData.categoryId) : undefined,
    subtype: apiData.subTypeId ? Subtype.getById(Number(apiData.subTypeId)) : null,
    type: apiData.type ? ContentFormat.getByTitle(apiData.type) : null,
    currentStatus:
      apiData.status !== undefined && apiData.status !== null
        ? EnrollmentStatus.getById(apiData.status)
        : undefined,
    loginIntegrationEnabled: apiData.loginIntegrationEnabled ?? null
  }
}

const transformCycle = (apiData: CycleAPI | null): Cycle | null => {
  if (!apiData) return null

  const formattedClassesStartDate =
    [apiData.classesStartDay, apiData.classesStartMonth, apiData.classesStartYear]
      .filter((value): value is number => value != null)
      .map(value => value.toString().padStart(2, '0'))
      .join('/') || ''

  return {
    id: apiData.id,
    name: apiData.name,
    registrationStart: formatDateString(apiData.registrationStart || ''),
    registrationEnd: formatDateString(apiData.registrationEnd || ''),
    enrollmentEnd: formatDateString(apiData.enrollmentEnd || ''),
    classesStartDay: apiData.classesStartDay,
    classesStartMonth: apiData.classesStartMonth,
    classesStartYear: apiData.classesStartYear,
    classesStartDate: formattedClassesStartDate
  }
}

const transformCourseDetailsInfo = (apiData: CourseDetailsInfoAPI): CourseDetailsInfo => {
  const formattedUnitInfo = apiData.unit ? `${apiData.unit.name}. ${apiData.unit.address}` : null

  const getUniqueNamesByType = (type: string): string[] => [
    ...new Set(
      apiData.languageFormats
        .filter(format => format.type?.toUpperCase() === type.toUpperCase())
        .map(format => format.name)
    )
  ]

  return {
    averageCourseDuration: apiData.averageCourseDuration ?? null,
    contentHours: apiData.contentHours ?? null,
    shift: apiData.shift ? Shift.getByName(apiData.shift)?.title : null,
    period: apiData.period ?? null,
    frequency: apiData.frequency ?? null,
    classSchedule: apiData.classSchedule
      ? formatTime(new Date(`2000-01-01T${apiData.classSchedule}`)) ?? null
      : null,
    classType: apiData.classType ? ContentFormat.getByTitle(apiData.classType)?.title : null,
    startDate: formatDateString(apiData.startDate ?? ''),
    registrationDeadline: formatDateString(apiData.registrationDeadline ?? ''),
    additionalInformation: apiData.additionalInformation ?? null,
    unit: formattedUnitInfo,
    teachingFormatName: apiData.teachingFormatName ?? null,
    teachingMaterialsPrice: apiData.teachingMaterialsPrice
      ? formatMoney(apiData.teachingMaterialsPrice)
      : null,
    languages: getUniqueNamesByType('LANGUAGE') || [],
    captions: getUniqueNamesByType('CAPTION') || []
  }
}

const transformSkill = (apiData: SkillAPI[]): Skill[] => {
  if (!apiData || apiData.length === 0) return []

  const formattedSkills = apiData.map(skill => ({
    id: skill.id ?? 0,
    name: skill.name ?? ''
  }))

  return formattedSkills
}

const transformRichTextMessage = (message: string): string => {
  if (!message || message.trim() === '') {
    return ''
  }

  let formattedMessage = message

  // Step 1: Convert URLs to clickable links
  // Pattern for URLs that are not already in <a> tags
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,\s])/gi
  formattedMessage = formattedMessage.replace(urlPattern, match => {
    return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="color: #00779d; text-decoration: underline; word-break: break-all;">${match}</a>`
  })

  // Step 2: Convert line breaks to HTML <br> tags
  // Handle both \r\n (Windows) and \n (Unix/Mac) line breaks
  formattedMessage = formattedMessage.replace(/\r\n/g, '<br>')
  formattedMessage = formattedMessage.replace(/\n/g, '<br>')

  // Step 3: Clean up multiple consecutive <br> tags (more than 2 in a row)
  formattedMessage = formattedMessage.replace(/(<br>){3,}/g, '<br><br>')

  // Step 4: Trim leading/trailing <br> tags
  formattedMessage = formattedMessage.replace(/^(<br>)+|(<br>)+$/g, '')

  return formattedMessage
}

export const transformTimeline = (timeline: EnrollmentTimelineStepAPI[]): EnrollmentStepItem[] => {
  return timeline.map((apiStep, index) => {
    return {
      id: `${apiStep.step}-${index}`,
      type: apiStep.step,
      title: getStepTitle(apiStep.step, apiStep.status),
      status: apiStep.status,
      message: transformRichTextMessage(apiStep.message ?? ''),
      startedTimeAt: apiStep.startedAt ? formatDateTime(apiStep.startedAt) : undefined,
      completedTimeAt: apiStep.completedAt ? formatDateTime(apiStep.completedAt) : undefined,
      startedAt: apiStep.startedAt ? formatDateString(apiStep.startedAt) : undefined,
      completedAt: apiStep.completedAt ? formatDateString(apiStep.completedAt) : undefined,
      leaderName: apiStep.leaderName ?? null,
      institutionName: apiStep.institutionName ?? null,
      contactEmail: apiStep.userContactEmail ?? null,
      analysisDaysDeadline: apiStep.analysisDaysDeadline ?? null,
      hasAction: apiStep.hasAction ?? null,
      hasBiometry: apiStep.hasBiometry ?? null,
      hasCycle: apiStep.hasCycle ?? null,
      limitDateEnrollment: apiStep.limitDateEnrollment
        ? formatDateString(apiStep.limitDateEnrollment)
        : undefined,
      limitDatePreEnrollment: apiStep.limitDatePreEnrollment
        ? formatDateString(apiStep.limitDatePreEnrollment)
        : undefined,
      cancelledBy: apiStep.cancelledBy ?? undefined
    }
  })
}

export const transformEnrollmentDetail = (
  apiData: EnrollmentDetailAPIResponse
): EnrollmentDetailDataResponse => ({
  authorizationInfo: transformAuthorizationInfo(apiData.authorizationInfo),
  courseDetailsInfo: transformCourseDetailsInfo(apiData.courseDetailsInfo),
  cycle: transformCycle(apiData.cycle),
  skills: transformSkill(apiData.skills),
  timeline: transformTimeline(apiData.timeline)
})
